<script setup>
import { ref, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['submit', 'back']);

const { t } = useI18n();
const store = useStore();

const configurableValues = ref({});
const isLoading = ref(false);

const handleSubmit = async () => {
  isLoading.value = true;
  emit('submit', {
    templateId: props.template.template_id,
    configurableValues: configurableValues.value,
  });
};

const handleBack = () => emit('back');
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Template Info (Read Only) -->
    <div class="rounded-lg border border-n-weak p-4 bg-n-alpha-1">
      <div class="flex items-center gap-2 mb-2">
        <span class="text-lg font-semibold text-n-slate-12">
          {{ template.title }}
        </span>
      </div>
      <p class="text-sm text-n-slate-11">
        {{ template.description }}
      </p>
    </div>

    <!-- Configurable Fields -->
    <div
      v-if="template.configurable_fields && template.configurable_fields.length"
      class="flex flex-col gap-3"
    >
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('CAPTAIN.CUSTOM_TOOLS.TEMPLATE.CONFIGURATION') }}
      </h3>
      <div
        v-for="field in template.configurable_fields"
        :key="field.field"
        class="flex flex-col gap-1"
      >
        <template v-if="field.type === 'select'">
          <label class="text-sm font-medium text-n-slate-12">
            {{ field.label }}
          </label>
          <select
            v-model="configurableValues[field.field]"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          >
            <option value="" disabled selected>
              {{ field.description }}
            </option>
            <option
              v-for="option in field.options"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </template>
        <Input
          v-else
          v-model="configurableValues[field.field]"
          :label="field.label"
          :placeholder="field.description"
          :type="field.type === 'password' ? 'password' : 'text'"
          :required="field.required"
        />
      </div>
    </div>

    <!-- Actions -->
    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.CUSTOM_TOOLS.TEMPLATE.BACK')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleBack"
      />
      <Button
        type="button"
        :label="t('CAPTAIN.FORM.CREATE')"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
